/**
 * Dashboard contracts (menu item: `dashboards`).
 *
 * A dashboard is a named canvas of cards laid out on a 12-column grid. Each card is a
 * saved, whitelisted query over one PSA entity: a set of typed filters plus either an
 * aggregation (metric / bar / line) or a column projection (list).
 *
 * SECURITY MODEL — cards are executed AS THE VIEWER, never as the author:
 *  - every entity declares an RBAC area (see `DASHBOARD_ENTITY_DEFS` in dashboard-fields.ts);
 *    a card whose area the viewer cannot read is omitted from the response server-side;
 *  - field keys, operators and sort keys are matched against the catalog and mapped to
 *    typed Prisma queries — no value is ever interpolated into raw SQL.
 *
 * Keep this file free of runtime framework dependencies (shared by backend, frontend, jobs).
 */
// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
/**
 * `personal` dashboards are visible only to their owner; `shared` dashboards are visible
 * to anyone who can see the Dashboards menu item (their cards are still viewer-scoped).
 */
export const DASHBOARD_VISIBILITIES = ['personal', 'shared'];
export const DASHBOARD_VISIBILITY_LABELS = {
    personal: 'Personal',
    shared: 'Shared',
};
/** Width of the layout grid. Card `w` is 1..12, `x` is 0..11. */
export const DASHBOARD_GRID_COLUMNS = 12;
/** Height of one grid row, in pixels (used by the frontend canvas). */
export const DASHBOARD_GRID_ROW_HEIGHT = 56;
/** Smallest allowed card footprint. */
export const DASHBOARD_CARD_MIN_W = 2;
export const DASHBOARD_CARD_MIN_H = 2;
export const DASHBOARD_MAX_CARDS = 40;
// ---------------------------------------------------------------------------
// Card types
// ---------------------------------------------------------------------------
export const DASHBOARD_CARD_TYPES = ['metric', 'list', 'bar', 'line', 'label'];
export const DASHBOARD_CARD_TYPE_LABELS = {
    metric: 'Metric',
    list: 'List',
    bar: 'Bar chart',
    line: 'Trend (line)',
    label: 'Label',
};
export const DASHBOARD_CARD_TYPE_DESCRIPTIONS = {
    metric: 'A single number — a count, sum or average — with optional colour thresholds.',
    list: 'A filtered table of records. Click a row to open the record.',
    bar: 'Counts or sums grouped by a field. Click a bar to see the records behind it.',
    line: 'A monthly trend over a date field.',
    label: 'A title bar — with optional text — that labels a section of cards.',
};
// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------
export const DASHBOARD_ENTITIES = [
    'projects',
    'tickets',
    'customers',
    'contacts',
    'onboarding',
    'agreements',
    'opportunities',
    'quotes',
    'invoices',
    'time_entries',
    'timesheets',
    'approvals',
    'billing_audit',
];
export const DASHBOARD_ENTITY_LABELS = {
    projects: 'Projects',
    tickets: 'Tickets',
    customers: 'Customers',
    contacts: 'Contacts',
    onboarding: 'Onboarding',
    agreements: 'Agreements',
    opportunities: 'Opportunities',
    quotes: 'Quotes',
    invoices: 'Invoices',
    time_entries: 'Time Entries',
    timesheets: 'Timesheets',
    approvals: 'Approvals',
    billing_audit: 'Billing Audit',
};
/**
 * Sentinel `entity` stored on label cards. Label cards run no query — they render only
 * their title and optional body text — so they carry no real entity or RBAC area.
 */
export const DASHBOARD_LABEL_ENTITY = 'label';
/**
 * Where a `uuid` field's picker values come from, so the card builder can render a
 * searchable dropdown instead of asking for a raw id.
 */
export const DASHBOARD_LOOKUPS = [
    'customer',
    'member',
    'service_category',
    'billing_class',
    'ticket_status',
    'ticket_type',
    'agreement',
    'agreement_subtype',
    'customer_type',
    'product',
];
// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------
export const DASHBOARD_OPERATORS = [
    'eq',
    'neq',
    'contains',
    'in',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'relative',
    'is_true',
    'is_false',
    'is_empty',
    'is_not_empty',
    'is_me',
    'is_not_me',
];
export const DASHBOARD_OPERATOR_LABELS = {
    eq: 'is',
    neq: 'is not',
    contains: 'contains',
    in: 'is any of',
    gt: 'is greater than',
    gte: 'is greater than or equal to',
    lt: 'is less than',
    lte: 'is less than or equal to',
    between: 'is between',
    relative: 'is within',
    is_true: 'is yes',
    is_false: 'is no',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
    is_me: 'is me',
    is_not_me: 'is not me',
};
/** Operators that take no value at all. */
export const DASHBOARD_VALUELESS_OPERATORS = [
    'is_true',
    'is_false',
    'is_empty',
    'is_not_empty',
    'is_me',
    'is_not_me',
];
/** Which operators each field type may use, in the order the builder should list them. */
export const DASHBOARD_OPERATORS_BY_TYPE = {
    string: ['contains', 'eq', 'neq', 'is_empty', 'is_not_empty'],
    number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
    money: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
    hours: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
    date: ['relative', 'lt', 'gt', 'between', 'eq', 'is_empty', 'is_not_empty'],
    datetime: ['relative', 'lt', 'gt', 'between', 'is_empty', 'is_not_empty'],
    boolean: ['is_true', 'is_false'],
    enum: ['eq', 'neq', 'in', 'is_empty', 'is_not_empty'],
    uuid: ['eq', 'neq', 'in', 'is_me', 'is_not_me', 'is_empty', 'is_not_empty'],
    link: ['is_true', 'is_false'],
    step: ['eq', 'neq'],
};
/**
 * Relative date windows, resolved server-side in the BILLING timezone so "today" means
 * today in Pacific, not UTC.
 */
export const DASHBOARD_RELATIVE_DATES = [
    'today',
    'yesterday',
    'this_week',
    'last_week',
    'this_month',
    'last_month',
    'this_quarter',
    'this_year',
    'last_7_days',
    'last_30_days',
    'last_60_days',
    'last_90_days',
    'next_7_days',
    'next_30_days',
    'next_90_days',
    'overdue',
    'future',
];
export const DASHBOARD_RELATIVE_DATE_LABELS = {
    today: 'Today',
    yesterday: 'Yesterday',
    this_week: 'This week',
    last_week: 'Last week',
    this_month: 'This month',
    last_month: 'Last month',
    this_quarter: 'This quarter',
    this_year: 'This year',
    last_7_days: 'Last 7 days',
    last_30_days: 'Last 30 days',
    last_60_days: 'Last 60 days',
    last_90_days: 'Last 90 days',
    next_7_days: 'Next 7 days',
    next_30_days: 'Next 30 days',
    next_90_days: 'Next 90 days',
    overdue: 'In the past (overdue)',
    future: 'In the future',
};
/** Statuses an onboarding checklist step can be filtered on. */
export const DASHBOARD_STEP_STATUSES = ['completed', 'skipped', 'pending'];
export const DASHBOARD_STEP_STATUS_LABELS = {
    completed: 'Completed',
    skipped: 'Skipped',
    pending: 'Not done yet',
};
/** How a set of conditions combine: every one must match, or any one may. */
export const DASHBOARD_FILTER_MATCHES = ['all', 'any'];
export const DASHBOARD_FILTER_MATCH_LABELS = {
    all: 'AND — all must match',
    any: 'OR — any may match',
};
/** Short form used between rows and groups in the builder. */
export const DASHBOARD_FILTER_MATCH_JOINERS = {
    all: 'AND',
    any: 'OR',
};
export const DASHBOARD_MAX_FILTER_GROUPS = 5;
export const DASHBOARD_MAX_FILTERS_PER_GROUP = 20;
/**
 * Resolves a card's conditions to groups. Cards saved before grouping existed carry a
 * flat `filters` list, which means exactly one all-must-match group.
 */
export function dashboardFilterGroups(config) {
    const groups = config.filterGroups ?? [];
    if (groups.length > 0)
        return groups;
    return [{ match: 'all', filters: config.filters ?? [] }];
}
// ---------------------------------------------------------------------------
// Card configuration
// ---------------------------------------------------------------------------
export const DASHBOARD_AGGREGATIONS = ['count', 'sum', 'avg', 'min', 'max'];
export const DASHBOARD_AGGREGATION_LABELS = {
    count: 'Count of records',
    sum: 'Sum of',
    avg: 'Average of',
    min: 'Smallest',
    max: 'Largest',
};
export const DASHBOARD_TONES = ['neutral', 'green', 'amber', 'red', 'blue'];
export const DASHBOARD_TONE_LABELS = {
    neutral: 'Default',
    green: 'Green',
    amber: 'Amber',
    red: 'Red',
    blue: 'Blue',
};
/** Threshold operators usable on a metric card (first match wins, in order). */
export const DASHBOARD_THRESHOLD_OPERATORS = ['gte', 'gt', 'lte', 'lt', 'eq'];
export const DASHBOARD_LIST_MAX_ROWS = 100;
export const DASHBOARD_LIST_DEFAULT_ROWS = 10;
export const DASHBOARD_GROUP_MAX = 25;
export const DASHBOARD_GROUP_DEFAULT = 8;
export const DASHBOARD_TREND_MAX_MONTHS = 24;
export const DASHBOARD_TREND_DEFAULT_MONTHS = 12;
export const DASHBOARD_DRILL_DOWN_MAX_ROWS = 200;
export const DASHBOARD_LABEL_BODY_MAX_LENGTH = 2000;
//# sourceMappingURL=dashboard.js.map